import { Link, useLocation } from "react-router-dom";
import { memo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import LanguageSelector from "./LanguageSelector";

const SITE_URL = "https://maxemployee.com";

const NavLink = memo(function NavLink({ to, children, testId, onClick }) {
  const { pathname } = useLocation();
  const isActive = pathname === to;
  return (
    <Link
      to={to}
      data-testId={testId}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={`text-xs font-mono uppercase tracking-[0.2em] transition-colors ${
        isActive ? "text-amber-500" : "text-zinc-400 hover:text-white focus-visible:text-white"
      } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm min-h-[44px] flex items-center`}
    >
      {children}
    </Link>
  );
});

const MobileNavLink = memo(function MobileNavLink({ to, children, onClick }) {
  const { pathname } = useLocation();
  const isActive = pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={`block text-sm font-mono uppercase tracking-[0.15em] transition-colors py-3 px-4 min-h-[44px] ${
        isActive ? "text-amber-500 bg-zinc-800/50" : "text-zinc-300 hover:text-white active:text-amber-500 hover:bg-zinc-800/30"
      }`}
    >
      {children}
    </Link>
  );
});

const BlogDropdown = memo(function BlogDropdown({ onItemClick }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const isBlogActive = pathname.startsWith("/blog") || pathname.startsWith("/community");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`text-xs font-mono uppercase tracking-[0.2em] transition-colors flex items-center gap-1 ${
          isBlogActive ? "text-amber-500" : "text-zinc-400 hover:text-white focus-visible:text-white"
        } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm min-h-[44px]`}
      >
        {t('nav.blog')}
        <svg className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 4l4 4 4-4" /></svg>
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded shadow-xl z-50" role="menu">
          <Link
            to="/blog"
            onClick={() => { setIsOpen(false); onItemClick?.(); }}
            role="menuitem"
            className={`block px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
              pathname.startsWith("/blog") ? "text-amber-500 bg-zinc-800" : "text-zinc-300 hover:text-white hover:bg-zinc-800"
            }`}
          >
            {t('nav.blog')}
          </Link>
          <Link
            to="/community"
            onClick={() => { setIsOpen(false); onItemClick?.(); }}
            role="menuitem"
            className={`block px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
              pathname.startsWith("/community") ? "text-amber-500 bg-zinc-800" : "text-zinc-300 hover:text-white hover:bg-zinc-800"
            }`}
          >
            {t('nav.community')}
          </Link>
        </div>
      )}
    </div>
  );
});

export default function Header() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isDemoArea = pathname.startsWith("/demo");

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <>
      <Helmet>
        <title>Max Employee | AI-Powered Team Management</title>
        <meta name="description" content={t('header.max_employee_is_an_ai_powered_platform_for_team')} />
        <link rel="canonical" href={SITE_URL} />
        <meta property="og:title" content={t('header.max_employee_ai_powered_team_management')} />
        <meta property="og:description" content={t('header.max_employee_is_an_ai_powered_platform_for_team')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={t('header.max_employee')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@maxemployee" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
      </Helmet>
      <header
        role="banner"
        data-testid="site-header"
        className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-14 sm:h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 sm:gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm shrink-0"
            aria-label={`${t('nav.home')} — Max Employee`}
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amber-500 flex items-center justify-center font-display text-black text-base sm:text-lg">
              M
            </div>
            <div className="font-display text-white text-sm sm:text-base tracking-tight uppercase">
              Max<span className="text-amber-500">/</span>Employee
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-10" role="navigation" aria-label={t('header.main_navigation')}>
            <NavLink to="/">{t('nav.home')}</NavLink>
            <NavLink to="/products">{t('nav.products')}</NavLink>
            <NavLink to="/marketplace">{t('nav.marketplace')}</NavLink>
            <NavLink to="/skills">{t('nav.skills')}</NavLink>
            <NavLink to="/cases">{t('nav.cases')}</NavLink>
            <BlogDropdown />
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSelector />
            {/* Mobile hamburger button */}
            <button
              type="button"
              className="md:hidden p-2 text-zinc-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-haspopup="true"
              aria-controls="mobile-menu"
              aria-label={t('header.open_navigation_menu')}
            >
              {mobileMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="18" x2="20" y2="18"></line></svg>
              )}
            </button>
            <Link to="/login" className="hidden sm:inline-block font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white px-3 sm:px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm min-h-[44px] flex items-center">{t('common.sign_in')}</Link>
            <Link to="/register" className="bg-amber-500 hover:bg-amber-400 text-black px-3 sm:px-5 py-1.5 sm:py-2.5 font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(245,158,11,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm min-h-[36px] sm:min-h-[44px] flex items-center whitespace-nowrap shrink-0">{t('common.start_free_trial')}</Link>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
            role="navigation"
            aria-label={t('header.mobile_navigation')}
          >
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col">
              <MobileNavLink to="/" onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</MobileNavLink>
              <MobileNavLink to="/products" onClick={() => setMobileMenuOpen(false)}>{t('nav.products')}</MobileNavLink>
              <MobileNavLink to="/marketplace" onClick={() => setMobileMenuOpen(false)}>{t('nav.marketplace')}</MobileNavLink>
              <MobileNavLink to="/skills" onClick={() => setMobileMenuOpen(false)}>{t('nav.skills')}</MobileNavLink>
              <MobileNavLink to="/cases" onClick={() => setMobileMenuOpen(false)}>{t('nav.cases')}</MobileNavLink>
              <div className="border-t border-white/10 my-2" />
              <MobileNavLink to="/blog" onClick={() => setMobileMenuOpen(false)}>{t('nav.blog')}</MobileNavLink>
              <MobileNavLink to="/community" onClick={() => setMobileMenuOpen(false)}>{t('nav.community')}</MobileNavLink>
              <div className="border-t border-white/10 my-2" />
              <MobileNavLink to="/login" onClick={() => setMobileMenuOpen(false)}>{t('common.sign_in')}</MobileNavLink>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
